<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { TranslatedGroupChart, TranslatedProduct } from "../types/PersologOnlineTypes.ts";
import { fetchLanguageOverviewData } from "../utils/LanguageOverviewUtils.ts";
import { getDisplayName, changeLanguage } from "../utils/GeneralUtils.ts";
import { isPublicLocale, useLocaleFromQuery } from "../composables/useLocaleFromQuery.ts";
import { i18n } from "../main.ts";
import PublicPageShell from "@persolog/ui/PublicPageShell.vue";
import TitleHeadline from "../components/common/TitleHeadline.vue";

const { t } = useI18n();
const route = useRoute();

const loading = ref<boolean>(false);
const error = ref<string>("");
const products = ref<TranslatedProduct[]>([]);
const groupCharts = ref<TranslatedGroupChart[]>([]);
const globalSearchText = ref<string>("");

// Which of the three tables to show (RDT-403). "all" keeps the page as it was before the filter.
type CategoryFilter = "all" | "profiles" | "questionnaires" | "groupCharts";
const categoryFilter = ref<CategoryFilter>("all");

const categoryOptions = computed(() => [
    { value: "all", label: t("languageOverview.filter.all") },
    { value: "profiles", label: t("languageOverview.filter.profiles") },
    { value: "questionnaires", label: t("languageOverview.filter.questionnaires") },
    { value: "groupCharts", label: t("languageOverview.filter.groupCharts") },
]);

const showProfiles = computed(() => categoryFilter.value === "all" || categoryFilter.value === "profiles");
const showQuestionnaires = computed(() => categoryFilter.value === "all" || categoryFilter.value === "questionnaires");
const showGroupCharts = computed(() => categoryFilter.value === "all" || categoryFilter.value === "groupCharts");

// The DE/EN switcher itself lives in PublicPageShell; this page only reacts to the change.
const activeLocale = computed(() => i18n.global.locale.value);

// Helper function to check if a row matches search in general fields (not languages)
const matchesGeneralSearch = (name: string, identifier: string, modelName: string, searchTerm: string) => {
    return name.toLowerCase().includes(searchTerm) ||
           identifier.toLowerCase().includes(searchTerm) ||
           modelName.toLowerCase().includes(searchTerm);
};

// Helper function to check if languages array contains search term
const matchesLanguageSearch = (languages: string[], searchTerm: string) => {
    return languages.some(lang =>
        lang.toLowerCase().includes(searchTerm) ||
        getDisplayName(i18n.global.locale.value, lang).toLowerCase().includes(searchTerm)
    );
};

const profileProducts = computed(() => {
    const filtered = products.value.filter(product =>
        product.profileLanguages && product.profileLanguages.length > 0
    );

    if (globalSearchText.value) {
        const searchTerm = globalSearchText.value.toLowerCase();
        return filtered.filter(product =>
            matchesGeneralSearch(product.productName, product.productNo, product.modelName, searchTerm) ||
            matchesLanguageSearch(product.profileLanguages, searchTerm)
        );
    }

    return filtered;
});

const questionnaireProducts = computed(() => {
    const filtered = products.value.filter(product =>
        product.questionnaireLanguages && product.questionnaireLanguages.length > 0
    );

    if (globalSearchText.value) {
        const searchTerm = globalSearchText.value.toLowerCase();
        return filtered.filter(product =>
            matchesGeneralSearch(product.productName, product.productNo, product.modelName, searchTerm) ||
            matchesLanguageSearch(product.questionnaireLanguages, searchTerm)
        );
    }

    return filtered;
});

const filteredGroupCharts = computed(() => {
    if (globalSearchText.value) {
        const searchTerm = globalSearchText.value.toLowerCase();
        return groupCharts.value.filter(groupChart =>
            matchesGeneralSearch(groupChart.groupChartName, groupChart.groupChartNo, groupChart.modelName, searchTerm) ||
            matchesLanguageSearch(groupChart.groupChartLanguages, searchTerm)
        );
    }

    return groupCharts.value;
});

// Only the categories the filter actually shows are counted, so the hit count matches the tables.
const totalFilteredEntries = computed(() => {
    const ids = new Set<string>();
    if (showProfiles.value) profileProducts.value.forEach(p => ids.add(p.productNo));
    if (showQuestionnaires.value) questionnaireProducts.value.forEach(p => ids.add(p.productNo));
    if (showGroupCharts.value) filteredGroupCharts.value.forEach(g => ids.add(g.groupChartNo));
    return ids.size;
});

const countLanguages = (languageLists: string[][]) => new Set(languageLists.flat()).size;

onMounted(async () => {
    // ?language=de|en deep link (shared with the other public pages)
    useLocaleFromQuery(route.query);

    await loadLanguageData();
});

async function loadLanguageData() {
    loading.value = true;
    error.value = "";

    try {
        const data = await fetchLanguageOverviewData();
        products.value = data.products;
        groupCharts.value = data.groupCharts;
    } catch (err) {
        console.error("Failed to load language overview data:", err);
        error.value = t("languageOverview.error.loadingFailed");
    } finally {
        loading.value = false;
    }
}

async function handleLanguageChange(language: string) {
    if (!isPublicLocale(language)) return;
    changeLanguage(language);
    // Clear search field when changing languages for fresh view
    globalSearchText.value = "";
    await loadLanguageData();
}
</script>

<template>
    <PublicPageShell
        id-prefix="language-overview"
        :active-locale="activeLocale"
        :switcher-label="t('languageOverview.languageSelector.label')"
        compact-header
        @locale-change="handleLanguageChange"
    >
        <template #logo>
            <a href="/">
                <img :alt="t('accessibility.images.logoPersolog')" src="../assets/persolog-logo.svg" height="40" class="h-10 w-auto logo" />
            </a>
        </template>

        <template #title>
            <TitleHeadline
                :title="t('languageOverview.heading')"
                :subtitle="t('languageOverview.subHeading')"
                :subtitle-position="'bottom'"
            />
        </template>

        <!-- Category Filter -->
        <div class="filter-container mb-3" data-cy="language-overview_category_filter">
            <SelectButton
                id="language-overview_category_filter"
                v-model="categoryFilter"
                :options="categoryOptions"
                option-label="label"
                option-value="value"
                :allow-empty="false"
                :aria-label="t('languageOverview.filter.label')"
            />
        </div>

        <!-- Global Search Field -->
        <div class="search-container mb-5">
            <div class="flex items-center gap-2">
                <IconField icon-position="left" class="flex-1">
                    <InputIcon class="pi pi-search" />
                    <InputText
                        v-model="globalSearchText"
                        :placeholder="t('languageOverview.search.placeholder')"
                        class="w-full"
                    />
                </IconField>
                <Button
                    v-if="globalSearchText"
                    icon="pi pi-times"
                    severity="secondary"
                    @click="globalSearchText = ''"
                    :aria-label="t('accessibility.buttons.clearSearch')"
                    class="p-button-text p-button-sm"
                />
            </div>
            <small v-if="globalSearchText" class="text-content-muted mt-2 block">
                {{ t('languageOverview.search.results', {
                    profileCount: showProfiles ? profileProducts.length : 0,
                    questionnaireCount: showQuestionnaires ? questionnaireProducts.length : 0,
                    groupChartCount: showGroupCharts ? filteredGroupCharts.length : 0,
                    total: totalFilteredEntries
                }) }}
            </small>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center p-6">
            <ProgressSpinner />
        </div>

        <!-- Error State -->
        <Message v-else-if="error" severity="error" :closable="false" class="mb-6">
            {{ error }}
        </Message>

        <!-- Main Content -->
        <div v-else class="grid grid-cols-12 gap-4">
            <!-- Profile Languages Table -->
            <div v-if="showProfiles" class="col-span-12 mb-6">
                <Card class="h-full">
                    <template #header>
                        <div class="flex items-center justify-between p-4 border-b border-border-ui">
                            <div class="flex items-center gap-2">
                                <i class="pi pi-user text-xl text-brand"></i>
                                <h3 class="m-0 text-xl font-semibold">{{ t('languageOverview.profileLanguages.title') }}</h3>
                            </div>
                        </div>
                    </template>

                    <template #content>
                        <DataTable
                            :value="profileProducts"
                            :paginator="true"
                            :rows="60"
                            :rows-per-page-options="[10, 20, 30, 60]"
                            :loading="loading"
                            sort-mode="multiple"
                            removable-sort
                            class="p-datatable-sm"
                            striped-rows
                            data-cy="language-overview_profile_table"
                        >

                            <Column
                                field="productNo"
                                :header="t('languageOverview.table.productId')"
                                :sortable="true"
                                class="whitespace-nowrap"
                                style="min-width: 100px"
                            >
                                <template #body="{ data }">
                                    <span class="font-mono font-semibold">{{ data.productNo }}</span>
                                </template>
                            </Column>

                            <Column
                                field="productName"
                                :header="t('languageOverview.table.productName')"
                                :sortable="true"
                                style="min-width: 200px"
                            >
                                <template #body="{ data }">
                                    <span class="font-medium">{{ data.productName }}</span>
                                </template>
                            </Column>

                            <Column
                                field="modelName"
                                :header="t('languageOverview.table.model')"
                                :sortable="true"
                                style="min-width: 150px"
                            >
                                <template #body="{ data }">
                                    <span class="text-content-muted">{{ data.modelName }}</span>
                                </template>
                            </Column>

                            <Column
                                field="profileLanguages"
                                :header="t('languageOverview.table.availableLanguages')"
                                :sortable="false"
                                style="min-width: 250px"
                            >
                                <template #body="{ data }">
                                    <div class="flex flex-wrap gap-1" v-if="data.profileLanguages?.length > 0">
                                        <Tag
                                            v-for="language in data.profileLanguages"
                                            :key="language"
                                            :value="getDisplayName(i18n.global.locale.value, language)"
                                            class="font-mono persolog-tag"
                                        />
                                    </div>
                                    <span v-else class="text-content-muted italic">
                                        {{ t('languageOverview.table.noLanguages') }}
                                    </span>
                                </template>
                            </Column>
                        </DataTable>
                    </template>
                </Card>
            </div>

            <!-- Questionnaire Languages Table -->
            <div v-if="showQuestionnaires" class="col-span-12 mb-6">
                <Card class="h-full">
                    <template #header>
                        <div class="flex items-center justify-between p-4 border-b border-border-ui">
                            <div class="flex items-center gap-2">
                                <i class="pi pi-pen-to-square text-xl text-brand"></i>
                                <h3 class="m-0 text-xl font-semibold">{{ t('languageOverview.questionnaireLanguages.title') }}</h3>
                            </div>
                        </div>
                    </template>

                    <template #content>
                        <DataTable
                            :value="questionnaireProducts"
                            :paginator="true"
                            :rows="60"
                            :rows-per-page-options="[10, 20, 30, 60]"
                            :loading="loading"
                            sort-mode="multiple"
                            removable-sort
                            class="p-datatable-sm"
                            striped-rows
                            data-cy="language-overview_questionnaire_table"
                        >

                            <Column
                                field="productNo"
                                :header="t('languageOverview.table.productId')"
                                :sortable="true"
                                class="whitespace-nowrap"
                                style="min-width: 100px"
                            >
                                <template #body="{ data }">
                                    <span class="font-mono font-semibold">{{ data.productNo }}</span>
                                </template>
                            </Column>

                            <Column
                                field="productName"
                                :header="t('languageOverview.table.productName')"
                                :sortable="true"
                                style="min-width: 200px"
                            >
                                <template #body="{ data }">
                                    <span class="font-medium">{{ data.productName }}</span>
                                </template>
                            </Column>

                            <Column
                                field="modelName"
                                :header="t('languageOverview.table.model')"
                                :sortable="true"
                                style="min-width: 150px"
                            >
                                <template #body="{ data }">
                                    <span class="text-content-muted">{{ data.modelName }}</span>
                                </template>
                            </Column>

                            <Column
                                field="questionnaireLanguages"
                                :header="t('languageOverview.table.availableLanguages')"
                                :sortable="false"
                                style="min-width: 250px"
                            >
                                <template #body="{ data }">
                                    <div class="flex flex-wrap gap-1" v-if="data.questionnaireLanguages?.length > 0">
                                        <Tag
                                            v-for="language in data.questionnaireLanguages"
                                            :key="language"
                                            :value="getDisplayName(i18n.global.locale.value, language)"
                                            class="font-mono persolog-tag"
                                        />
                                    </div>
                                    <span v-else class="text-content-muted italic">
                                        {{ t('languageOverview.table.noLanguages') }}
                                    </span>
                                </template>
                            </Column>
                        </DataTable>
                    </template>
                </Card>
            </div>

            <!-- Group Chart Languages Table (RDT-403) -->
            <div v-if="showGroupCharts" class="col-span-12">
                <Card class="h-full">
                    <template #header>
                        <div class="flex items-center justify-between p-4 border-b border-border-ui">
                            <div class="flex items-center gap-2">
                                <i class="pi pi-chart-bar text-xl text-brand"></i>
                                <h3 class="m-0 text-xl font-semibold">{{ t('languageOverview.groupChartLanguages.title') }}</h3>
                            </div>
                        </div>
                    </template>

                    <template #content>
                        <DataTable
                            :value="filteredGroupCharts"
                            :paginator="true"
                            :rows="60"
                            :rows-per-page-options="[10, 20, 30, 60]"
                            :loading="loading"
                            sort-mode="multiple"
                            removable-sort
                            class="p-datatable-sm"
                            striped-rows
                            data-cy="language-overview_group_chart_table"
                        >

                            <Column
                                field="groupChartNo"
                                :header="t('languageOverview.table.productId')"
                                :sortable="true"
                                class="whitespace-nowrap"
                                style="min-width: 100px"
                            >
                                <template #body="{ data }">
                                    <span class="font-mono font-semibold">{{ data.groupChartNo }}</span>
                                </template>
                            </Column>

                            <Column
                                field="groupChartName"
                                :header="t('languageOverview.table.productName')"
                                :sortable="true"
                                style="min-width: 200px"
                            >
                                <template #body="{ data }">
                                    <span class="font-medium">{{ data.groupChartName }}</span>
                                </template>
                            </Column>

                            <Column
                                field="modelName"
                                :header="t('languageOverview.table.model')"
                                :sortable="true"
                                style="min-width: 150px"
                            >
                                <template #body="{ data }">
                                    <span class="text-content-muted">{{ data.modelName }}</span>
                                </template>
                            </Column>

                            <Column
                                field="groupChartLanguages"
                                :header="t('languageOverview.table.availableLanguages')"
                                :sortable="false"
                                style="min-width: 250px"
                            >
                                <template #body="{ data }">
                                    <div class="flex flex-wrap gap-1" v-if="data.groupChartLanguages?.length > 0">
                                        <Tag
                                            v-for="language in data.groupChartLanguages"
                                            :key="language"
                                            :value="getDisplayName(i18n.global.locale.value, language)"
                                            class="font-mono persolog-tag"
                                        />
                                    </div>
                                    <span v-else class="text-content-muted italic">
                                        {{ t('languageOverview.table.noLanguages') }}
                                    </span>
                                </template>
                            </Column>
                        </DataTable>
                    </template>
                </Card>
            </div>
        </div>

        <!-- Summary Statistics -->
        <Card class="mt-6">
            <template #content>
                <div class="grid grid-cols-12 gap-4 text-center">
                    <template v-if="showProfiles">
                        <div class="col-span-12 md:col-span-3 lg:col-span-2">
                            <div class="p-4 rounded-lg bg-primary-50">
                                <i class="pi pi-user text-brand text-2xl mb-2 block"></i>
                                <h4 class="m-0 text-brand">{{ profileProducts.length }}</h4>
                                <p class="text-brand text-sm m-0">{{ t('languageOverview.stats.productsWithProfiles') }}</p>
                            </div>
                        </div>
                        <div class="col-span-12 md:col-span-3 lg:col-span-2">
                            <div class="p-4 rounded-lg bg-primary-50">
                                <i class="pi pi-globe text-brand text-2xl mb-2 block"></i>
                                <h4 class="m-0 text-brand">
                                    {{ countLanguages(profileProducts.map(p => p.profileLanguages)) }}
                                </h4>
                                <p class="text-brand text-sm m-0">{{ t('languageOverview.stats.profileLanguagesTotal') }}</p>
                            </div>
                        </div>
                    </template>
                    <template v-if="showQuestionnaires">
                        <div class="col-span-12 md:col-span-3 lg:col-span-2">
                            <div class="p-4 rounded-lg bg-primary-50">
                                <i class="pi pi-list text-brand text-2xl mb-2 block"></i>
                                <h4 class="m-0 text-brand">{{ questionnaireProducts.length }}</h4>
                                <p class="text-brand text-sm m-0">{{ t('languageOverview.stats.productsWithQuestionnaires') }}</p>
                            </div>
                        </div>
                        <div class="col-span-12 md:col-span-3 lg:col-span-2">
                            <div class="p-4 rounded-lg bg-primary-50">
                                <i class="pi pi-globe text-brand text-2xl mb-2 block"></i>
                                <h4 class="m-0 text-brand">
                                    {{ countLanguages(questionnaireProducts.map(p => p.questionnaireLanguages)) }}
                                </h4>
                                <p class="text-brand text-sm m-0">{{ t('languageOverview.stats.questionnaireLanguagesTotal') }}</p>
                            </div>
                        </div>
                    </template>
                    <template v-if="showGroupCharts">
                        <div class="col-span-12 md:col-span-3 lg:col-span-2">
                            <div class="p-4 rounded-lg bg-primary-50">
                                <i class="pi pi-chart-bar text-brand text-2xl mb-2 block"></i>
                                <h4 class="m-0 text-brand">{{ filteredGroupCharts.length }}</h4>
                                <p class="text-brand text-sm m-0">{{ t('languageOverview.stats.groupCharts') }}</p>
                            </div>
                        </div>
                        <div class="col-span-12 md:col-span-3 lg:col-span-2">
                            <div class="p-4 rounded-lg bg-primary-50">
                                <i class="pi pi-globe text-brand text-2xl mb-2 block"></i>
                                <h4 class="m-0 text-brand">
                                    {{ countLanguages(filteredGroupCharts.map(g => g.groupChartLanguages)) }}
                                </h4>
                                <p class="text-brand text-sm m-0">{{ t('languageOverview.stats.groupChartLanguagesTotal') }}</p>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
        </Card>
    </PublicPageShell>
</template>

<style scoped>

/* Page layout, the header row and the language switcher now live in PublicPageShell. */
.logo {
    cursor: pointer;
    transition: opacity 0.2s ease;
}

.logo:hover {
    opacity: 0.8;
}

.filter-container {
    display: flex;
    justify-content: center;
}

/* margin-inline, not the `margin` shorthand: the shorthand resets the mb-* utility to 0 and the
   search field then sits flush against the first table header. */
.search-container {
    max-width: 500px;
    margin-inline: auto;
}

:deep(Button:active .lang),
:deep(Button:hover .lang) {
    color: var(--p-surface-0);
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
    padding: 0.75rem 0.5rem;
}

:deep(.p-tag) {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
}

:deep(.persolog-tag) {
    background-color: var(--p-primary-500) !important;
    color: white !important;
    margin-left: 0.2rem;
    margin-top:0.2rem;
}

:deep(.p-card .p-card-header) {
    padding: 0;
    background-color: var(--p-secondary-500) !important;
    color: white !important;
}

:deep(.p-card .p-card-header .text-primary) {
    color: white !important;
}

:deep(.p-card .p-card-header h3) {
    color: white !important;
}

:deep(.p-card .p-card-content) {
    padding: 0;
}

@media (max-width: 768px) {
    .language-overview {
        padding: 1rem 0.5rem;
    }

    .filter-container :deep(.p-selectbutton) {
        flex-wrap: wrap;
        justify-content: center;
    }

    :deep(.p-datatable .p-datatable-tbody > tr > td) {
        padding: 0.5rem 0.25rem;
        font-size: 0.875rem;
    }

    :deep(.p-tag) {
        font-size: 0.625rem;
        padding: 0.2rem 0.4rem;
    }

    :deep(.persolog-tag) {
        background-color: var(--p-primary-500) !important;
        color: white !important;
        border-radius: 1rem !important;
        border: none !important;
    }
}
</style>
